import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { FlatfinderPoint, FlatfinderRegion } from 'realitiq-utils/src/flatfinder';
import {
  calculateZoomedView,
  convertClientPointToElementPoint,
  type AreaEdgeCandidate,
  type PendingAreaEdge,
  type PolygonEditorView,
} from './polygon-editor-utils';

export function useHoveredEstateSynchronization({
  regions,
  hoveredRegionId,
  selectionEnabled,
  onHoverEstate,
}: {
  regions: FlatfinderRegion[];
  hoveredRegionId: string | null;
  selectionEnabled: boolean;
  onHoverEstate?: (id: string | null) => void;
}) {
  useEffect(() => {
    onHoverEstate?.(
      selectionEnabled
        ? (regions.find((region) => region.id === hoveredRegionId)?.estateId ?? null)
        : null
    );
    return () => onHoverEstate?.(null);
  }, [hoveredRegionId, onHoverEstate, regions, selectionEnabled]);
}

export function useDrawingChangeNotification(
  pointCount: number,
  onDrawingChange?: (dirty: boolean) => void
) {
  useEffect(() => {
    onDrawingChange?.(pointCount > 0);
  }, [onDrawingChange, pointCount]);

  useEffect(() => () => onDrawingChange?.(false), [onDrawingChange]);
}

export function useSelectedAreaEdgeInvalidation({
  regions,
  selectedAreaId,
  pendingEdge,
  setHoveredEdge,
}: {
  regions: FlatfinderRegion[];
  selectedAreaId: string | null;
  pendingEdge: MutableRefObject<PendingAreaEdge | null>;
  setHoveredEdge: Dispatch<SetStateAction<AreaEdgeCandidate | null>>;
}) {
  useEffect(() => {
    setHoveredEdge((current) =>
      current && current.region.id === selectedAreaId && regions.includes(current.region)
        ? current
        : null
    );
    if (
      pendingEdge.current &&
      (pendingEdge.current.edge.region.id !== selectedAreaId ||
        !regions.includes(pendingEdge.current.edge.region))
    ) {
      pendingEdge.current = null;
    }
  }, [pendingEdge, regions, selectedAreaId, setHoveredEdge]);
}

export function useSelectedVertexReset(
  selectedAreaId: string | null,
  setSelectedVertex: Dispatch<SetStateAction<number | null>>
) {
  const previousSelectedAreaId = useRef(selectedAreaId);

  useEffect(() => {
    if (previousSelectedAreaId.current === selectedAreaId) return;
    setSelectedVertex(null);
    previousSelectedAreaId.current = selectedAreaId;
  }, [selectedAreaId, setSelectedVertex]);
}

export function usePolygonEditorScreenScale(
  svg: RefObject<SVGSVGElement | null>,
  imageWidth: number,
  imageHeight: number
) {
  const [screenScale, setScreenScale] = useState(1);

  useEffect(() => {
    const element = svg.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      setScreenScale(Math.max(0.001, Math.min(rect.width / imageWidth, rect.height / imageHeight)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [imageHeight, imageWidth, svg]);

  return screenScale;
}

export function useDrawingKeyboardShortcuts({
  drawing,
  undonePoints,
  drawingEnabled,
  setDrawing,
  setUndonePoints,
  cancelDrawing,
}: {
  drawing: FlatfinderPoint[];
  undonePoints: FlatfinderPoint[];
  drawingEnabled: boolean;
  setDrawing: Dispatch<SetStateAction<FlatfinderPoint[]>>;
  setUndonePoints: Dispatch<SetStateAction<FlatfinderPoint[]>>;
  cancelDrawing: () => void;
}) {
  useEffect(() => {
    const handleDrawingKeyboardShortcut = (event: KeyboardEvent) => {
      if (!drawingEnabled) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && ['z', 'y'].includes(event.key.toLowerCase())) {
        const redoRequested = event.shiftKey || event.key.toLowerCase() === 'y';
        if (redoRequested ? !undonePoints.length : !drawing.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (redoRequested) {
          const point = undonePoints[0];
          if (point) {
            setDrawing([...drawing, point]);
            setUndonePoints(undonePoints.slice(1));
          }
        } else {
          const point = drawing.at(-1);
          if (point) {
            setDrawing(drawing.slice(0, -1));
            setUndonePoints([point, ...undonePoints]);
          }
        }
      }

      if (event.key === 'Escape') cancelDrawing();
    };

    window.addEventListener('keydown', handleDrawingKeyboardShortcut, true);
    return () => window.removeEventListener('keydown', handleDrawingKeyboardShortcut, true);
  }, [cancelDrawing, drawing, drawingEnabled, setDrawing, setUndonePoints, undonePoints]);
}

export function usePolygonEditorWheelZoom({
  svg,
  setView,
  clearHoveredEdge,
}: {
  svg: RefObject<SVGSVGElement | null>;
  setView: Dispatch<SetStateAction<PolygonEditorView>>;
  clearHoveredEdge: () => void;
}) {
  useEffect(() => {
    const element = svg.current;
    if (!element) return;

    function handleWheelZoom(event: WheelEvent) {
      event.preventDefault();
      const focusPoint = convertClientPointToElementPoint(element, {
        x: event.clientX,
        y: event.clientY,
      });
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1);
      setView((currentView) =>
        calculateZoomedView(currentView, focusPoint, currentView.zoom * Math.exp(-delta * 0.002))
      );
      clearHoveredEdge();
    }

    element.addEventListener('wheel', handleWheelZoom, { passive: false });
    return () => element.removeEventListener('wheel', handleWheelZoom);
  }, [clearHoveredEdge, setView, svg]);
}
